import { useEffect } from 'react'
import type { JSX, RefObject } from 'react'
import { formatTime } from '../lib/format'

export type VideoPlayerProps = {
  src: string | null
  videoRef: RefObject<HTMLVideoElement | null>
  currentTime: number
  duration: number
  onTimeChange: (time: number) => void
  onDurationChange: (duration: number) => void
  disabledHint?: string
}

export function VideoPlayer({
  src,
  videoRef,
  currentTime,
  duration,
  onTimeChange,
  onDurationChange,
  disabledHint,
}: VideoPlayerProps): JSX.Element {
  // Плавный playhead: timeupdate шлётся 4 раза в секунду, для таймлайна мало.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let raf = 0
    let stopped = false
    const tick = (): void => {
      if (stopped) return
      onTimeChange(video.currentTime)
      raf = window.requestAnimationFrame(tick)
    }
    const onPlay = (): void => {
      window.cancelAnimationFrame(raf)
      raf = window.requestAnimationFrame(tick)
    }
    const onStop = (): void => {
      window.cancelAnimationFrame(raf)
      onTimeChange(video.currentTime)
    }
    // timeupdate только сообщает время — гасить rAF-цикл здесь нельзя.
    const onSync = (): void => onTimeChange(video.currentTime)
    video.addEventListener('play', onPlay)
    video.addEventListener('playing', onPlay)
    video.addEventListener('pause', onStop)
    video.addEventListener('ended', onStop)
    video.addEventListener('seeked', onSync)
    video.addEventListener('timeupdate', onSync)
    video.addEventListener('loadedmetadata', onSync)
    if (!video.paused) onPlay()
    return () => {
      stopped = true
      window.cancelAnimationFrame(raf)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('playing', onPlay)
      video.removeEventListener('pause', onStop)
      video.removeEventListener('ended', onStop)
      video.removeEventListener('seeked', onSync)
      video.removeEventListener('timeupdate', onSync)
      video.removeEventListener('loadedmetadata', onSync)
    }
  }, [videoRef, onTimeChange, src])

  const seekBy = (delta: number): void => {
    const video = videoRef.current
    if (!video) return
    const max = Number.isFinite(video.duration) ? video.duration : duration
    video.currentTime = Math.min(Math.max(0, video.currentTime + delta), Math.max(0, max))
  }

  const togglePlay = (): void => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) void video.play().catch(() => undefined)
    else video.pause()
  }

  return (
    <div className="video-shell">
      {src ? (
        <video
          ref={videoRef}
          src={src}
          controls
          preload="metadata"
          playsInline
          onLoadedMetadata={(e) => {
            const value = e.currentTarget.duration
            if (Number.isFinite(value)) onDurationChange(value)
          }}
          onDurationChange={(e) => {
            const value = e.currentTarget.duration
            if (Number.isFinite(value)) onDurationChange(value)
          }}
        />
      ) : (
        <div className="video-placeholder">
          {disabledHint ?? 'Видео ещё не загружено. Загрузите файл, чтобы начать разметку.'}
        </div>
      )}
      <div className="video-bar">
        <button type="button" onClick={togglePlay} disabled={!src} title="Пробел">
          ⏯ Плей / пауза
        </button>
        <button type="button" className="ghost" onClick={() => seekBy(-1)} disabled={!src}>
          −1 с
        </button>
        <button type="button" className="ghost" onClick={() => seekBy(-0.1)} disabled={!src}>
          −0,1 с
        </button>
        <button type="button" className="ghost" onClick={() => seekBy(0.1)} disabled={!src}>
          +0,1 с
        </button>
        <button type="button" className="ghost" onClick={() => seekBy(1)} disabled={!src}>
          +1 с
        </button>
        <span className="spacer" />
        <span className="mono">
          {formatTime(currentTime)} <span className="faint">/ {formatTime(duration)}</span>
        </span>
      </div>
    </div>
  )
}
